import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { useResume } from '../context/ResumeContext';
import { SAMPLE_RESUME_TEXT, SAMPLE_DATA } from '../constants/sampleData';
import { TOOL_FONTS_CSS } from '../constants/templates';
import { parseResumeWithAPI } from '../utils/parseResume';
import { EMPTY_RESUME } from '../constants/sampleData';
import { ReactComponent as Logo } from '../logo.svg';
import AdSlot from '../components/ui/AdSlot';

export default function LandingPage() {
  const { rawText, setRawText, setData, setParseError } = useResume();
  const [isParsing, setIsParsing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Free Resume Builder — Create a Professional Resume in Minutes';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Build a professional, ATS-friendly resume for free. Paste your existing resume or start from scratch, choose from 12 expert-designed templates, customize colors, and download as PDF or Word. No sign-up required.'
    );
  }, []);

  const handleParse = async () => {
    if (!rawText.trim()) return;
    setIsParsing(true);
    setParseError(null);
    try {
      const parsed = await parseResumeWithAPI(rawText);
      setData(parsed);
      navigate('/editor');
    } catch (err) {
      console.error('Parse error:', err);
      setParseError("We couldn't parse this — paste the structured fields manually.");
      setData(EMPTY_RESUME);
      navigate('/editor');
    } finally {
      setIsParsing(false);
    }
  };

  const handleSample = () => {
    setRawText(SAMPLE_RESUME_TEXT);
    setData(SAMPLE_DATA);
    navigate('/editor');
  };

  const handleStartFromScratch = () => {
    setData(EMPTY_RESUME);
    navigate('/editor');
  };

  if (isParsing) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ background: '#FAF7F0' }}>
        <style>{TOOL_FONTS_CSS}</style>
        <div className="text-center" role="status" aria-live="polite">
          <Loader2 className="w-10 h-10 text-stone-700 animate-spin mx-auto mb-5" />
          <div className="tool-display text-2xl text-stone-800 italic">Reading your résumé…</div>
          <div className="tool-body text-sm text-stone-500 mt-2">This usually takes a few seconds.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ background: '#FAF7F0' }}>
      <style>{TOOL_FONTS_CSS}</style>
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <Logo className="w-10 h-10" aria-hidden="true" />
            <span className="tool-body text-[11px] uppercase tracking-[0.3em] text-stone-500 font-medium">Free online resume builder</span>
          </div>
          <h1 className="tool-display text-5xl md:text-6xl text-stone-900 leading-[1.05] tracking-tight">
            Free Resume Builder.<br/>
            <span className="italic text-stone-700">Professional templates, instant PDF.</span>
          </h1>
          <p className="tool-body text-stone-600 mt-5 max-w-xl leading-relaxed">
            Create a professional resume in minutes — completely free, no sign-up required.
            Paste your existing résumé as plain text, choose from <strong>12 ATS-friendly templates</strong> across
            6 industries, customize accent colors, and <strong>download as PDF or Word</strong>.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleStartFromScratch}
              className="tool-body bg-stone-900 hover:bg-stone-700 text-white px-6 py-3 rounded text-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              Create Your Resume Now
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#paste-resume"
              onClick={(e) => { e.preventDefault(); document.getElementById('resume-input')?.focus(); }}
              className="tool-body border border-stone-300 hover:border-stone-500 text-stone-700 hover:text-stone-900 px-6 py-3 rounded text-sm font-medium tracking-wide text-center transition-colors"
            >
              I have a résumé to paste
            </a>
          </div>
        </header>

        <section aria-label="Resume input">
          <div className="bg-white rounded-md border border-stone-300/80 shadow-sm overflow-hidden">
            <label htmlFor="resume-input" className="sr-only">Paste your resume text here</label>
            <textarea
              id="resume-input"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder="Paste your résumé here..."
              className="tool-body w-full px-5 py-4 text-sm text-stone-800 focus:outline-none resize-none"
              style={{ minHeight: '320px', fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
              aria-describedby="resume-help"
            />
            <div id="resume-help" className="sr-only">
              Paste your plain-text resume and click Parse to convert it into a beautifully formatted document.
            </div>
            <div className="border-t border-stone-200 px-4 py-3 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-stone-50/50">
              <button
                onClick={handleSample}
                className="tool-body text-sm text-stone-600 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-600 transition-colors"
              >
                Try with a sample résumé
              </button>
              <button
                onClick={handleParse}
                disabled={!rawText.trim()}
                className="tool-body bg-stone-900 hover:bg-stone-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded text-sm font-medium tracking-wide flex items-center gap-2 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Parse résumé
              </button>
            </div>
          </div>
        </section>

        <section className="mt-12" aria-label="Features">
          <h2 className="sr-only">Resume Builder Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center tool-body">
            <div>
              <div className="text-2xl font-semibold text-stone-900">12</div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">Professional Templates</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-stone-900">6</div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">Industry Categories</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-stone-900">100%</div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">Free, No Sign-Up</div>
            </div>
          </div>
          <div className="mt-6 text-xs text-stone-500 tool-body text-center">
            <span>Corporate · Tech · Creative · Healthcare/Education · Academic · Trades</span>
          </div>
        </section>

        <section className="mt-16" aria-label="How it works">
          <h2 className="tool-display text-2xl text-stone-900 mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 tool-body text-sm text-stone-700">
            <div>
              <div className="text-lg font-semibold text-stone-900 mb-1">1. Paste</div>
              <p>Paste your existing resume as plain text — our AI parser extracts your experience, education, skills, and contact info automatically.</p>
            </div>
            <div>
              <div className="text-lg font-semibold text-stone-900 mb-1">2. Customize</div>
              <p>Choose from 12 professionally designed templates. Adjust accent colors, review parsed content, and edit anything in real time.</p>
            </div>
            <div>
              <div className="text-lg font-semibold text-stone-900 mb-1">3. Download</div>
              <p>Export your polished resume as a PDF or Word file. All templates are ATS-friendly with a real text layer for applicant tracking systems.</p>
            </div>
          </div>
        </section>

        <div className="mt-14 mb-2">
          <AdSlot
            adSlot=""
            adFormat="horizontal"
            className="max-w-2xl mx-auto opacity-90"
          />
        </div>
      </div>

      <footer className="border-t border-stone-200 mt-8 py-8 text-center tool-body text-xs text-stone-400">
        <p>Free Resume Builder — Create professional, ATS-friendly resumes. No sign-up required.</p>
      </footer>
    </div>
  );
}
